import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const PersonalSection = () => {
  const interests = [
  {
    icon: "BookOpen",
    title: "Continuous Learning",
    description: "I dedicate 10+ hours weekly to learning new technologies, reading technical blogs, and staying ahead of industry trends."
  },
  {
    icon: "Users",
    title: "Community Building",
    description: "Active in developer communities, mentoring junior developers, and contributing to open-source projects that solve real problems."
  },
  {
    icon: "Lightbulb",
    title: "Problem Solving",
    description: "I approach challenges like puzzles—breaking them down, finding patterns, and crafting elegant solutions that others can understand."
  },
  {
    icon: "TrendingUp",
    title: "Business Strategy",
    description: "Fascinated by how technology drives business growth. I study successful startups to understand what makes technical solutions truly valuable."
  }];


  const certifications = [
  { name: "AWS Certified Solutions Architect", year: "2023", icon: "Award" },
  { name: "Google Cloud Professional Developer", year: "2023", icon: "Award" },
  { name: "MongoDB Certified Developer", year: "2022", icon: "Award" },
  { name: "React Advanced Patterns", year: "2022", icon: "Award" },
  { name: "Node.js Application Development", year: "2021", icon: "Award" },
  { name: "Full-Stack Web Development", year: "2020", icon: "Award" }];


  return (
    <section className="py-16 md:py-24 lg:py-32 bg-card">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 mb-16 md:mb-24">
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6">
              <Icon name="Heart" size={16} className="text-primary" />
              <span className="text-sm font-medium text-primary">Beyond the Code</span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-heading text-foreground mb-6">
              What Drives Me
            </h2>
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-8">
              I'm not just a developer who codes—I'm a problem solver who happens to use code as my primary tool. My passion lies in understanding complex challenges and crafting solutions that create real value.
            </p>

            <div className="space-y-6">
              {interests?.map((interest, index) =>
              <div key={index} className="flex gap-4 group">
                  <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors duration-300">
                    <Icon name={interest?.icon} size={24} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-foreground mb-1">{interest?.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{interest?.description}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-8">
            <div className="relative rounded-2xl overflow-hidden border-4 border-primary/20 shadow-xl">
              <Image
                src="https://images.unsplash.com/photo-1573377843702-426d10da9054"
                alt="Modern developer workspace with dual monitors displaying code editor and design tools, mechanical keyboard, coffee mug, and minimalist desk setup with plants"
                className="w-full h-[300px] md:h-[400px] object-cover" />

            </div>

            <div className="bg-background rounded-xl p-6 md:p-8 border border-border">
              <div className="flex items-center gap-3 mb-6">
                <Icon name="MapPin" size={24} className="text-primary" />
                <div>
                  <div className="text-sm text-muted-foreground">Based in</div>
                  <div className="text-lg font-bold text-foreground">Lagos, Nigeria</div>
                </div>
              </div>
              <div className="flex items-center gap-3 mb-6">
                <Icon name="Globe" size={24} className="text-primary" />
                <div>
                  <div className="text-sm text-muted-foreground">Working with clients</div>
                  <div className="text-lg font-bold text-foreground">Globally</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Icon name="Clock" size={24} className="text-primary" />
                <div>
                  <div className="text-sm text-muted-foreground">Timezone</div>
                  <div className="text-lg font-bold text-foreground">WAT (UTC+1)</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div>
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-4">
              <Icon name="GraduationCap" size={16} className="text-primary" />
              <span className="text-sm font-medium text-primary">Professional Growth</span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-heading text-foreground mb-4">
              Certifications & Learning
            </h2>
            <p className="text-base md:text-lg text-muted-foreground max-w-3xl mx-auto">
              Continuous learning isn't just a buzzword for me—it's a commitment. Here are some of the certifications I've earned to stay at the cutting edge.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {certifications?.map((cert, index) =>
            <div
              key={index}
              className="bg-background rounded-xl p-6 border border-border hover:border-primary/50 transition-all duration-300 hover:shadow-lg">

                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Icon name={cert?.icon} size={24} className="text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-bold text-foreground mb-1 line-clamp-2">{cert?.name}</h3>
                    <span className="text-sm text-muted-foreground">{cert?.year}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>);

};

export default PersonalSection;